import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  Building2,
  ChevronDown,
  Columns3,
  CornerDownLeft,
  Folder,
  LayoutList,
  ListFilter,
  Lock,
  Plus,
} from 'lucide-react'
import {
  DEFAULT_VIEW_ICON,
  VIEW_ICONS,
  ViewIcon,
} from './viewIcons'
import {
  VIEW_DESCRIPTION_MAX,
  VIEW_NAME_MAX,
  buildLayoutConfig,
  columnNames,
  countConditions,
  layoutSummaryKeys,
  resolveViewName,
  suggestViewName,
} from './viewDraft'
import { showViewToast } from './viewToast'
import type { ReactNode } from 'react'
import type { TaskFilter } from '@/lib/types/filter'
import type { SavedFilterPermission, SavedFilterScope } from '@/lib/types/savedFilter'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import type { ProjectViewPreferences } from '@/store/ProjectViewPreferencesStore'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useCustomFieldDefinitions,
  useProjects,
  useSavedFilterMutations,
  useStarredMutations,
} from '@/lib/db/rxdb/hooks'
import { serializeSavedFilterConfig } from '@/lib/types/savedFilter'
import { useFilterLabels } from '@/components/features/filters/useFilterLabels'
import useLoginStore from '@/store/LoginStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { cn } from '@/lib/utils'

export interface SaveViewDefaults {
  name?: string
  description?: string
  icon?: string | null
  permission?: SavedFilterPermission
}

interface SaveViewPopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The button(s) the popover hangs from. */
  children: ReactNode
  filters: ReadonlyArray<TaskFilter>
  scope: SavedFilterScope
  /** Project or space id (null for a workspace-wide view). */
  scopeEntityId: string | null
  /** Pre-fill for "Save as new view…" from an existing view. */
  defaults?: SaveViewDefaults
  onSaved?: (view: SavedFilterDocument) => void
  align?: 'start' | 'center' | 'end'
  /** The child toggles the popover itself (a plain button), not just anchors it. */
  asTrigger?: boolean
}

const EMPTY_PREFS: Partial<ProjectViewPreferences> = {}

/**
 * "Save as view" (design 20a): a popover, not a modal. The name is read off
 * the filters and selected so typing replaces it; Save is never disabled.
 */
export function SaveViewPopover({
  open,
  onOpenChange,
  children,
  filters,
  scope,
  scopeEntityId,
  defaults,
  onSaved,
  align = 'end',
  asTrigger = false,
}: SaveViewPopoverProps) {
  const { t } = useTranslation('tasks')
  const { t: tCommon } = useTranslation('common')
  const userId = useLoginStore((s) => s.userId)
  const { valueSummary } = useFilterLabels()
  const { projects } = useProjects({ isDeleted: false })
  const { customFieldDefinitions } = useCustomFieldDefinitions()
  const { createSavedFilter, deleteSavedFilter } = useSavedFilterMutations()
  const { createStar, deleteStar } = useStarredMutations()
  const prefs = useProjectViewPreferencesStore(
    (s) => (scopeEntityId ? s.preferences[scopeEntityId] : undefined) ?? EMPTY_PREFS,
  )

  const nameRef = useRef<HTMLInputElement | null>(null)
  const [showDescription, setShowDescription] = useState(false)
  const [iconMenuOpen, setIconMenuOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const place = useMemo(() => {
    if (scope === 'global' || !scopeEntityId) return null
    return projects.find((p) => p.id === scopeEntityId)?.name ?? null
  }, [projects, scope, scopeEntityId])

  const suggested = useMemo(
    () =>
      suggestViewName(filters, valueSummary, {
        or: t('filters.groups.or'),
        not: t('savedFilters.save.not'),
      }),
    [filters, valueSummary, t],
  )

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .max(VIEW_NAME_MAX, t('savedFilters.save.nameTooLong', { max: VIEW_NAME_MAX })),
        description: z
          .string()
          .max(VIEW_DESCRIPTION_MAX, t('savedFilters.save.descriptionTooLong', { max: VIEW_DESCRIPTION_MAX })),
        icon: z.string().max(50),
        saveLayout: z.boolean(),
        saveColumns: z.boolean(),
        permission: z.enum(['private', 'workspace', 'project']),
        pin: z.boolean(),
      }),
    [t],
  )
  type FormValues = z.infer<typeof schema>

  const initialValues = (): FormValues => ({
    name: defaults?.name ?? suggested,
    description: defaults?.description ?? '',
    icon: defaults?.icon ?? DEFAULT_VIEW_ICON,
    saveLayout: true,
    saveColumns: true,
    permission: defaults?.permission ?? 'private',
    pin: true,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues(),
  })
  const values = useWatch({ control: form.control })

  // Every open starts from a fresh suggestion.
  useEffect(() => {
    if (!open) return
    const next = initialValues()
    form.reset(next)
    setShowDescription(next.description.length > 0)
  }, [open])

  // Without a place, "Only me" and "Workspace" are the only choices.
  const permission: SavedFilterPermission =
    !place && values.permission === 'project' ? 'private' : (values.permission ?? 'private')

  const { groups, conditions } = countConditions(filters)
  const filtersSummary = groups > 0
    ? `${t('savedFilters.save.groups', { count: groups })} · ${t('savedFilters.save.conditions', { count: conditions })}`
    : t('savedFilters.save.conditions', { count: conditions })

  const layoutKeys = layoutSummaryKeys(prefs)
  const layoutSummary = [
    t(`savedFilters.save.viewType.${layoutKeys.viewType}`),
    t(`savedFilters.save.groupBy.${layoutKeys.groupBy}`),
    t(`savedFilters.save.sortBy.${layoutKeys.sortBy}`),
  ].join(' · ')

  const fieldNames = useMemo(
    () => new Map(customFieldDefinitions.filter((d) => !d.isDeleted).map((d) => [d.id, d.name])),
    [customFieldDefinitions],
  )
  const columns = columnNames(prefs, fieldNames)
  const columnsSummary = [
    t(`savedFilters.save.density.${prefs.density ?? 'simple'}`),
    columns.length > 0 ? columns.join(', ') : t('savedFilters.save.defaultColumns'),
  ].join(' · ')

  const visibilityOptions: Array<{
    value: SavedFilterPermission
    label: string
    icon: typeof Lock
  }> = [
    { value: 'private', label: t('savedFilters.visibility.private'), icon: Lock },
    ...(place
      ? [{ value: 'project' as const, label: place, icon: Folder }]
      : []),
    { value: 'workspace', label: t('savedFilters.visibility.workspace'), icon: Building2 },
  ]

  const explanation =
    permission === 'workspace'
      ? t('savedFilters.visibility.workspaceHint')
      : permission === 'project' && place
        ? t(scope === 'space' ? 'savedFilters.visibility.spaceHint' : 'savedFilters.visibility.projectHint', { place })
        : place
          ? t('savedFilters.visibility.privateHintPlace', { place })
          : t('savedFilters.visibility.privateHint')

  const onSubmit = async (data: FormValues) => {
    if (!userId || isSaving) return
    setIsSaving(true)
    try {
      const name = resolveViewName(data.name, suggested, t('savedFilters.save.untitled'))
      const description = data.description.trim()
      const created = await createSavedFilter({
        id: crypto.randomUUID(),
        name,
        description: description.length > 0 ? description : null,
        filterConfig: serializeSavedFilterConfig([...filters]),
        layoutConfig: buildLayoutConfig(prefs, {
          layout: data.saveLayout,
          columns: data.saveColumns,
        }),
        scope,
        scopeEntityId,
        permission,
        ownerId: userId,
        icon: data.icon,
        sortOrder: null,
      })

      let starId: string | null = null
      if (data.pin) {
        try {
          const star = await createStar({
            userId,
            entityType: 'saved_filter',
            entityId: created.id,
            sortOrder: null,
          })
          starId = star.id
        } catch {
          // The view is saved; pinning is a nicety.
        }
      }

      const where =
        permission === 'workspace'
          ? t('savedFilters.toast.savedToWorkspace')
          : permission === 'project' && place
            ? t('savedFilters.toast.savedToPlace', { place })
            : t('savedFilters.toast.savedForYou')
      showViewToast(
        starId ? t('savedFilters.toast.savedPinned', { where }) : where,
        t('savedFilters.toast.undo'),
        () => {
          void (async () => {
            try {
              if (starId) await deleteStar(starId)
              await deleteSavedFilter(created.id)
            } catch {
              toast.error(t('savedFilters.toast.undoFailed'))
            }
          })()
        },
      )
      onSaved?.(created)
      onOpenChange(false)
    } catch {
      toast.error(t('savedFilters.toast.viewSaveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const { ref: nameFieldRef, ...nameField } = form.register('name')
  const nameError = form.formState.errors.name?.message
  const descriptionError = form.formState.errors.description?.message

  const rowClass = 'flex min-h-6 items-center gap-2'
  const rowLabel = 'text-foreground'
  const rowValue = 'ml-auto min-w-0 truncate text-right text-muted-foreground'

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {asTrigger ? (
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      ) : (
        <PopoverAnchor asChild>{children}</PopoverAnchor>
      )}
      <PopoverContent
        align={align}
        sideOffset={6}
        className="w-[min(400px,calc(100vw-32px))] rounded-xl border-border bg-card p-0 text-[13px] shadow-lg"
        onFocusOutside={(e) => {
          // Opened from a menu ("Save as new view…"): the closing menu grabs focus once.
          if (e.target instanceof Element && e.target.closest('[role="menu"]')) e.preventDefault()
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          nameRef.current?.focus()
          nameRef.current?.select()
        }}
        aria-label={t('filters.saveAsView')}
      >
        <form
          noValidate
          onSubmit={(e) => {
            e.stopPropagation()
            void submit(e)
          }}
        >
          <div className="space-y-3 p-4">
            {/* Icon + name */}
            <div className="flex items-start gap-2">
              <Popover open={iconMenuOpen} onOpenChange={setIconMenuOpen}>
                <PopoverAnchor asChild>
                  <button
                    type="button"
                    aria-label={t('savedFilters.save.chooseIcon')}
                    aria-haspopup="dialog"
                    aria-expanded={iconMenuOpen}
                    onClick={() => setIconMenuOpen((o) => !o)}
                    className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <ViewIcon icon={values.icon} className="size-4" />
                    <span className="absolute -right-1 -bottom-1 flex size-3.5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground">
                      <ChevronDown className="size-2.5" />
                    </span>
                  </button>
                </PopoverAnchor>
                <PopoverContent
                  align="start"
                  className="grid w-auto grid-cols-6 gap-1 rounded-xl border-border bg-card p-2"
                  aria-label={t('savedFilters.save.chooseIcon')}
                >
                  {Object.entries(VIEW_ICONS).map(([key, Icon]) => (
                    <button
                      key={key}
                      type="button"
                      aria-label={key}
                      aria-pressed={values.icon === key}
                      onClick={() => {
                        form.setValue('icon', key, { shouldDirty: true })
                        setIconMenuOpen(false)
                        nameRef.current?.focus()
                      }}
                      className={cn(
                        'flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground',
                        values.icon === key && 'bg-brand-tint text-brand',
                      )}
                    >
                      <Icon className="size-4" />
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              <div className="min-w-0 flex-1">
                <Input
                  {...nameField}
                  ref={(el) => {
                    nameFieldRef(el)
                    nameRef.current = el
                  }}
                  maxLength={VIEW_NAME_MAX}
                  placeholder={suggested || t('savedFilters.save.untitled')}
                  aria-label={t('savedFilters.save.nameLabel')}
                  aria-invalid={!!nameError}
                  aria-describedby="save-view-name-hint"
                  className="h-9 rounded-lg bg-card text-[14px] font-medium selection:bg-brand-tint selection:text-foreground md:text-[14px]"
                />
                {(nameError || !defaults?.name) && (
                  <p
                    id="save-view-name-hint"
                    className={cn('mt-1.5 text-[11.5px]', nameError ? 'text-destructive' : 'text-muted-foreground')}
                  >
                    {nameError ?? (suggested ? t('savedFilters.save.nameHint') : t('savedFilters.save.nameHintEmpty'))}
                  </p>
                )}
              </div>
            </div>

            {/* What gets saved */}
            <div className="space-y-1 rounded-lg border border-border bg-secondary/60 px-3 py-2 text-[12.5px]">
              <div className={rowClass}>
                <ListFilter className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className={rowLabel}>{t('savedFilters.save.filters')}</span>
                <span className={rowValue}>{filtersSummary}</span>
              </div>
              <label className={rowClass}>
                <LayoutList className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className={cn(rowLabel, 'shrink-0')}>{t('savedFilters.save.layout')}</span>
                <span className={rowValue}>{layoutSummary}</span>
                <Switch
                  checked={values.saveLayout ?? true}
                  onCheckedChange={(v) => form.setValue('saveLayout', v, { shouldDirty: true })}
                  aria-label={t('savedFilters.save.layout')}
                />
              </label>
              <label className={rowClass}>
                <Columns3 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className={cn(rowLabel, 'shrink-0')}>{t('savedFilters.save.columns')}</span>
                <span className={rowValue}>{columnsSummary}</span>
                <Switch
                  checked={values.saveColumns ?? true}
                  onCheckedChange={(v) => form.setValue('saveColumns', v, { shouldDirty: true })}
                  aria-label={t('savedFilters.save.columns')}
                />
              </label>
            </div>

            {/* Who can see it */}
            <div>
              <p id="save-view-visibility" className="mb-1.5 text-[12.5px] font-semibold">
                {t('savedFilters.visibility.heading')}
              </p>
              <div
                role="radiogroup"
                aria-labelledby="save-view-visibility"
                className="flex gap-0.5 rounded-lg bg-secondary p-0.5"
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                  e.preventDefault()
                  const i = visibilityOptions.findIndex((o) => o.value === permission)
                  const step = e.key === 'ArrowRight' ? 1 : -1
                  const next = visibilityOptions[(i + step + visibilityOptions.length) % visibilityOptions.length]
                  if (next) form.setValue('permission', next.value, { shouldDirty: true })
                }}
              >
                {visibilityOptions.map((opt) => {
                  const selected = permission === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => form.setValue('permission', opt.value, { shouldDirty: true })}
                      className={cn(
                        'flex h-7 min-w-0 flex-auto items-center justify-center gap-1.5 rounded-md px-2.5 text-[12.5px] transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        selected
                          ? 'bg-card font-medium text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <opt.icon className="size-3.5 shrink-0" aria-hidden />
                      <span className="truncate">{opt.label}</span>
                    </button>
                  )
                })}
              </div>
              <p className="mt-1.5 text-[11.5px] leading-snug text-muted-foreground">{explanation}</p>
            </div>

            <label className="flex items-center justify-between gap-2 text-[12.5px]">
              <span>{t('savedFilters.save.pin')}</span>
              <Switch
                checked={values.pin ?? true}
                onCheckedChange={(v) => form.setValue('pin', v, { shouldDirty: true })}
              />
            </label>

            {showDescription && (
              <div>
                <Textarea
                  {...form.register('description')}
                  autoFocus={!defaults?.description}
                  maxLength={VIEW_DESCRIPTION_MAX}
                  placeholder={t('savedFilters.saveDialog.descriptionPlaceholder')}
                  aria-label={t('savedFilters.save.descriptionLabel')}
                  aria-invalid={!!descriptionError}
                  className="min-h-14 rounded-lg bg-card text-[12.5px] md:text-[12.5px]"
                  onKeyDown={(e) => {
                    // Enter saves here too; Shift+Enter is a new line.
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault()
                      e.currentTarget.form?.requestSubmit()
                    }
                  }}
                />
                {descriptionError && (
                  <p className="mt-1 text-[11.5px] text-destructive">{descriptionError}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-border px-4 py-3">
            {!showDescription && (
              <button
                type="button"
                onClick={() => setShowDescription(true)}
                className="flex items-center gap-1 rounded-md text-[12.5px] text-muted-foreground hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none"
              >
                <Plus className="size-3.5" aria-hidden />
                {t('savedFilters.save.addDescription')}
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-lg bg-card px-3 text-[12.5px] shadow-none"
                onClick={() => onOpenChange(false)}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="h-8 gap-1.5 rounded-lg bg-foreground px-3 text-[12.5px] font-semibold text-background hover:bg-foreground/90"
              >
                {t('savedFilters.saveDialog.saveView')}
                <CornerDownLeft className="size-3 opacity-70" aria-hidden />
              </Button>
            </div>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
